import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { Agency } from '@/lib/pocketbase'

// Fetch agencies list
export function useAgencies(filters?: {
  search?: string
  province?: string
  page?: number
  perPage?: number
}) {
  const { search, province, page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['agencies', filters],
    queryFn: async () => {
      const filterParts: string[] = []

      if (search) {
        filterParts.push(
          pb.filter('(name_th ~ {:search} || name_en ~ {:search})', { search })
        )
      }
      if (province) {
        filterParts.push(pb.filter('province = {:province}', { province }))
      }
      const filter = filterParts.join(' && ')

      const result = await pb.collection('agencies').getList<Agency>(page, perPage, {
        filter,
      })
      return {
        ...result,
        items: result.items.map((item) => ({
          ...item,
          name: item.name || item.name_th || item.name_en || '',
          province_code: item.province_code || item.province || '',
        })),
      }
    },
  })
}

// Fetch single agency
export function useAgency(agencyId: string) {
  return useQuery({
    queryKey: ['agency', agencyId],
    queryFn: async () => {
      const agency = await pb.collection('agencies').getOne<Agency>(agencyId)
      return {
        ...agency,
        name: agency.name || agency.name_th || agency.name_en || '',
      }
    },
    enabled: !!agencyId,
  })
}

// Fetch agency's tenders
export function useAgencyTenders(agencyId: string, page = 1) {
  return useQuery({
    queryKey: ['agency-tenders', agencyId, page],
    queryFn: async () => {
      const result = await pb.collection('v_tenders_public').getList(page, 20, {
        filter: `agency = "${agencyId}"`,
        sort: '-published_at',
        expand: 'agency',
      })

      return {
        ...result,
        items: result.items.map((item: any) => ({
          ...item,
          tender_id: item.tender || item.id,
          project_name: item.title_th,
          dept_name: item.expand?.agency?.name_th || item.expand?.agency?.name_en || '',
          project_status: item.project_status,
          sum_price_agree: item.procured_amount_satang ?? item.budget_amount_satang,
        })),
      }
    },
    enabled: !!agencyId,
  })
}
